/**
 * Entrada de estoque e desdobro.
 *
 * As duas telas que faltavam para o pátio sair do papel — as rotas já existiam,
 * a interface não.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  camposDaRegra, cubar, oQueMede, rotuloDaRegra, Volume,
  type RegraCubagem,
} from "@madeira/dominio";
import { api, type Filial, type Parceiro, type Produto } from "../api.ts";

type Medida = Record<string, string>;

function CamposMedida({
  regra, valores, aoMudar, prefixo,
}: {
  regra: RegraCubagem;
  valores: Medida;
  aoMudar: (chave: string, valor: string) => void;
  prefixo: string;
}) {
  return (
    <>
      {camposDaRegra(regra).map((c) => (
        <label key={c.chave} style={{ display: "block", marginBottom: 10 }}>
          <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>{c.rotulo}</span>
          <input
            className="campo-texto num" inputMode="decimal"
            value={valores[c.chave] ?? ""}
            onChange={(e) => aoMudar(c.chave, e.target.value)}
            aria-label={`${prefixo} ${c.rotulo}`}
          />
        </label>
      ))}
    </>
  );
}

function volumeDe(regra: RegraCubagem, medida: Medida): Volume | null {
  try { return cubar(regra, medida); } catch { return null; }
}

/* -------------------------------------------------------------- entrada */

export function Entrada({
  filiais, parceiros, produtos, aviso,
}: {
  filiais: Filial[]; parceiros: Parceiro[]; produtos: Produto[];
  aviso: (t: string) => void;
}) {
  const fornecedores = useMemo(
    () => parceiros.filter((p) => p.papel === "FORNECEDOR" || p.papel === "AMBOS"),
    [parceiros],
  );
  const [parceiroId, setParceiroId] = useState(() => fornecedores[0]?.id ?? "");
  const [produtoId, setProdutoId] = useState(() => produtos.find((p) => p.ativo)?.id ?? "");
  const [lote, setLote] = useState("");
  const [medida, setMedida] = useState<Medida>({});
  /**
   * Quanto se pagou por esta entrada, no total.
   *
   * Opcional porque tora de floresta própria não tem custo de aquisição. Mas
   * sem ele o volume entra na conta da margem SEM o custo do lado, e a margem
   * por espécie passa a parecer certa sem ser — que é pior do que não mostrar.
   * Por isso a tela avisa em vez de aceitar calado.
   */
  const [valorTotal, setValorTotal] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const fornecedor = fornecedores.find((p) => p.id === parceiroId);
  /* Mesma porta do orçamento: a regra vem do contrato do fornecedor (§2.4). */
  const regra = (fornecedor?.regraCubagem ?? "SERRADA") as RegraCubagem;
  const volume = volumeDe(regra, medida);

  /** Só calcula quando os dois lados existem — dividir por zero mostraria ∞. */
  const custoPorM3 = (() => {
    const v = Number(valorTotal.replace(",", "."));
    if (!volume || !Number.isFinite(v) || v <= 0) return null;
    const m3 = Number(volume.paraBanco());
    return m3 > 0 ? v / m3 : null;
  })();

  async function gravar() {
    if (!lote.trim()) { aviso("Informe o código do lote — toda entrada carrega lote"); return; }
    if (!volume) { aviso("Preencha as medidas"); return; }
    setOcupado(true);
    try {
      const r = await api.post<{
        volumeM3: string; loteId: string; custoRegistrado: boolean;
      }>("/estoque/entradas", {
        filialId: filiais[0]?.id, produtoId, parceiroId, medida, loteCodigo: lote.trim(),
        // Ausente, não vazio: o serviço distingue "não informado" de zero.
        ...(valorTotal.trim() ? { valorTotal: valorTotal.trim().replace(",", ".") } : {}),
      });
      const m3 = Number(r.volumeM3);
      aviso(
        `Entrada de ${m3.toFixed(3)} m³ no lote ${lote.trim()}` +
        (r.custoRegistrado
          ? ` · R$ ${(Number(valorTotal.replace(",", ".")) / m3)
              .toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/m³`
          : " · SEM custo — fica de fora da margem"),
      );
      setMedida({});
      setLote("");
      setValorTotal("");
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha na entrada");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="painel" style={{ maxWidth: 560 }}>
      <div className="tt"><span className="rotulo">Entrada de estoque</span></div>
      <div style={{ padding: 16 }}>
        <label style={{ display: "block", marginBottom: 10 }}>
          <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>Fornecedor</span>
          <select className="campo-texto foco-claro" value={parceiroId} onChange={(e) => setParceiroId(e.target.value)}>
            {fornecedores.map((p) => (
              <option key={p.id} value={p.id}>{p.nome} — cubagem {p.regraCubagem}</option>
            ))}
          </select>
        </label>

        <div className="contexto" style={{ margin: "0 0 12px", borderRadius: 3, border: "1px solid var(--acao-tinta-borda)" }}>
          <span className="rotulo">Cubagem</span>
          <b>{rotuloDaRegra(regra)}</b>
          <span>· do contrato de {fornecedor?.nome ?? "—"} · medindo {oQueMede(regra)}</span>
        </div>

        <label style={{ display: "block", marginBottom: 10 }}>
          <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>Produto</span>
          <select className="campo-texto foco-claro" value={produtoId} onChange={(e) => setProdutoId(e.target.value)}>
            {produtos.filter((p) => p.ativo).map((p) => (
              <option key={p.id} value={p.id}>{p.codigo} — {p.especie} {p.forma.toLowerCase()}</option>
            ))}
          </select>
        </label>

        <label style={{ display: "block", marginBottom: 10 }}>
          <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>Lote</span>
          <input
            className="campo-texto mono" value={lote}
            onChange={(e) => setLote(e.target.value)} placeholder="L-0001"
          />
        </label>

        <CamposMedida regra={regra} valores={medida} prefixo="entrada"
          aoMudar={(k, v) => setMedida((m) => ({ ...m, [k]: v }))} />

        <label style={{ display: "block", marginTop: 10 }}>
          <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>
            Valor pago (R$) — opcional
          </span>
          <input
            className="campo-texto num" inputMode="decimal" style={{ width: 160 }}
            value={valorTotal} onChange={(e) => setValorTotal(e.target.value)}
            placeholder="7800,00"
          />
          <span className="cod" style={{ display: "block", marginTop: 4 }}>
            {valorTotal.trim()
              ? "Entra na margem por espécie."
              : "Em branco: o volume entra no estoque, mas fica fora da conta de margem."}
          </span>
        </label>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--linha-grade)",
        }}>
          <div>
            <span className="rotulo" style={{ display: "block" }}>Volume</span>
            <span className="num" style={{ fontSize: 24, fontWeight: 600 }}>
              {volume ? `${volume.paraExibicao()} m³` : "—"}
            </span>
            {/* O custo por m³ é o número que o comprador de tora negocia —
                o valor total sozinho não diz se o negócio foi bom. */}
            {custoPorM3 !== null && (
              <span className="cod" style={{ display: "block", marginTop: 2 }}>
                R$ {custoPorM3.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2, maximumFractionDigits: 2,
                })} por m³
              </span>
            )}
          </div>
          <button className="bt" onClick={() => void gravar()} disabled={ocupado || !volume}>
            Registrar entrada
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- desdobro */

interface LoteComSaldo {
  id: string; codigo: string; produto_id: string; produto: string;
  forma: string; especie: string; saldo: string;
  regra: string | null; medida: Record<string, unknown> | null;
}

interface Saida { chave: number; produtoId: string; medida: Medida }
let proxima = 1;

export function Desdobro({
  filiais, produtos, aviso,
}: {
  filiais: Filial[]; produtos: Produto[]; aviso: (t: string) => void;
}) {
  const serradas = produtos.filter((p) => p.ativo && p.forma !== "TORA");

  /**
   * O lote traz TUDO consigo.
   *
   * Antes esta tela pedia o UUID digitado à mão e mandava redigitar a regra e
   * a medida da tora — que o banco já guarda desde a entrada. Redigitar não é
   * conferência: um dígito errado e o consumo não bate com o que entrou, o
   * rendimento sai torto e ninguém percebe.
   *
   * Agora escolhe-se o lote e os três vêm juntos, somente-leitura.
   */
  const [lotes, setLotes] = useState<LoteComSaldo[]>([]);
  const [loteId, setLoteId] = useState("");
  const lote = lotes.find((l) => l.id === loteId) ?? null;

  const carregarLotes = useCallback(() => {
    void api.get<{ lotes: LoteComSaldo[] }>("/lotes?forma=TORA")
      .then((r) => {
        setLotes(r.lotes);
        // Mantém a escolha se o lote ainda tem saldo; senão cai no primeiro.
        setLoteId((atual) =>
          r.lotes.some((l) => l.id === atual) ? atual : r.lotes[0]?.id ?? "",
        );
      })
      .catch(() => {});
  }, []);
  useEffect(carregarLotes, [carregarLotes]);

  const produtoToraId = lote?.produto_id ?? "";
  const regraTora = (lote?.regra ?? "FRANCON") as RegraCubagem;
  /* A medida vem do lançamento de entrada; números chegam como string. */
  const medidaTora: Medida = useMemo(() => {
    if (!lote?.medida) return {};
    return Object.fromEntries(
      Object.entries(lote.medida).map(([k, v]) => [k, String(v)]),
    );
  }, [lote]);
  const [saidas, setSaidas] = useState<Saida[]>(() => [
    { chave: proxima++, produtoId: serradas[0]?.id ?? "", medida: {} },
  ]);
  const [ocupado, setOcupado] = useState(false);

  const volTora = volumeDe(regraTora, medidaTora);
  const volSaidas = saidas.map((s) => volumeDe("SERRADA", s.medida));
  const totalSaida = volSaidas.reduce<Volume>(
    (acc, v) => (v ? acc.mais(v) : acc), Volume.ZERO,
  );

  /* Rendimento é o KPI que o dono olha todo dia (§2.6) — mostrado ao vivo. */
  const rendimento =
    volTora && volTora.ehPositivo() && !totalSaida.ehZero()
      ? (Number(totalSaida.paraBanco()) / Number(volTora.paraBanco())) * 100
      : null;
  const impossivel = volTora ? Number(totalSaida.paraBanco()) > Number(volTora.paraBanco()) : false;

  async function gravar() {
    if (!loteId.trim()) { aviso("Informe o lote de tora consumido"); return; }
    setOcupado(true);
    try {
      const r = await api.post<{ rendimentoPercentual: string; perdaM3: string }>(
        "/estoque/desdobros",
        {
          filialId: filiais[0]?.id, loteId: loteId.trim(), produtoToraId,
          regraTora, medidaTora,
          saidas: saidas.map((s) => ({ produtoId: s.produtoId, regra: "SERRADA", medida: s.medida })),
        },
      );
      aviso(`Desdobro gravado · rendimento ${r.rendimentoPercentual}% · perda ${Number(r.perdaM3).toFixed(3)} m³`);
      // O lote foi consumido: some da lista sozinho, porque a rota só devolve
      // lote com saldo. Recarregar é o que reflete isso.
      carregarLotes();
      setSaidas([{ chave: proxima++, produtoId: serradas[0]?.id ?? "", medida: {} }]);
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha no desdobro");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="painel" style={{ maxWidth: 900 }}>
      <div className="tt"><span className="rotulo">Desdobro — consome tora, produz serrada</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 20, padding: 16 }}>
        <div>
          <span className="rotulo" style={{ display: "block", marginBottom: 8 }}>Entrada · tora</span>

          <label style={{ display: "block", marginBottom: 10 }}>
            <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>
              Lote consumido
            </span>
            <select
              className="campo-texto foco-claro" value={loteId}
              onChange={(e) => setLoteId(e.target.value)}
            >
              {lotes.length === 0 && <option value="">nenhum lote com saldo</option>}
              {lotes.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.codigo} — {l.produto} · {Number(l.saldo).toFixed(3)} m³
                </option>
              ))}
            </select>
          </label>

          {/*
            Regra e medida MOSTRADAS, nunca pedidas de novo — mesma decisão do
            orçamento (design §6.1): o sistema exibe o que já sabe. O operador
            confere; não redigita.
          */}
          {lote && (
            <div
              className="contexto"
              style={{ margin: "0 0 12px", borderRadius: 3, border: "1px solid var(--acao-tinta-borda)", display: "block" }}
            >
              <div>
                <span className="rotulo">Cubagem</span>{" "}
                <b>{rotuloDaRegra(regraTora)}</b>
                <span> · como este lote entrou</span>
              </div>
              <div className="mono" style={{ marginTop: 4, fontSize: 12.5 }}>
                {camposDaRegra(regraTora)
                  .map((c) => `${c.rotulo} ${medidaTora[c.chave] ?? "—"}`)
                  .join("  ·  ")}
              </div>
            </div>
          )}

          <div style={{ fontSize: 12.5, color: "var(--texto-terciario)" }}>
            Consome o lote inteiro, na medida em que entrou. Para consumir
            parcial, registre a sobra como entrada nova.
          </div>
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <span className="rotulo">Saídas · serrada</span>
            <button
              className="bt bt-neutro" style={{ marginLeft: "auto", padding: "3px 9px", fontSize: 12 }}
              onClick={() => setSaidas((s) => [...s, { chave: proxima++, produtoId: serradas[0]?.id ?? "", medida: {} }])}
            >
              + saída
            </button>
          </div>
          {saidas.map((s, i) => (
            <div key={s.chave} style={{
              border: "1px solid var(--linha-grade)", borderRadius: 3,
              padding: 10, marginBottom: 8,
            }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <select
                  className="campo-texto foco-claro" style={{ flex: 1 }}
                  value={s.produtoId}
                  onChange={(e) => setSaidas((ls) => ls.map((x, k) => k === i ? { ...x, produtoId: e.target.value } : x))}
                >
                  {serradas.map((p) => <option key={p.id} value={p.id}>{p.codigo}</option>)}
                </select>
                {saidas.length > 1 && (
                  <button
                    className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                    onClick={() => setSaidas((ls) => ls.filter((_, k) => k !== i))}
                  >
                    remover
                  </button>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {camposDaRegra("SERRADA").map((c) => (
                  <label key={c.chave}>
                    <span className="rotulo" style={{ display: "block", marginBottom: 3, fontSize: 9 }}>{c.rotulo}</span>
                    <input
                      className="campo-texto num" inputMode="decimal"
                      value={s.medida[c.chave] ?? ""}
                      onChange={(e) => setSaidas((ls) => ls.map((x, k) =>
                        k === i ? { ...x, medida: { ...x.medida, [c.chave]: e.target.value } } : x))}
                    />
                  </label>
                ))}
              </div>
              <div className="num" style={{ marginTop: 6, fontSize: 13, color: "var(--texto-secundario)" }}>
                {volSaidas[i]?.paraExibicao() ?? "—"} m³
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        display: "flex", alignItems: "center", gap: 24,
        padding: "12px 16px", borderTop: "1px solid var(--borda-forte)",
        background: "var(--faixa-resumo)",
      }}>
        <div>
          <span className="rotulo" style={{ display: "block" }}>Tora</span>
          <span className="num" style={{ fontSize: 20, fontWeight: 600 }}>{volTora?.paraExibicao() ?? "—"}</span>
        </div>
        <div>
          <span className="rotulo" style={{ display: "block" }}>Serrada</span>
          <span className="num" style={{ fontSize: 20, fontWeight: 600 }}>{totalSaida.paraExibicao()}</span>
        </div>
        <div>
          <span className="rotulo" style={{ display: "block" }}>Rendimento</span>
          <span className="num" style={{
            fontSize: 20, fontWeight: 600,
            color: impossivel ? "var(--erro-texto)" : rendimento && rendimento < 45 ? "var(--aten-num)" : "var(--ok-num)",
          }}>
            {rendimento === null ? "—" : `${rendimento.toFixed(1)}%`}
          </span>
        </div>
        {impossivel && (
          <span className="pil p-erro"><i />sai mais do que entra — confira as bitolas</span>
        )}
        <button
          className="bt" style={{ marginLeft: "auto" }}
          onClick={() => void gravar()} disabled={ocupado || !volTora || totalSaida.ehZero() || impossivel}
        >
          Registrar desdobro
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------ fila de pendências */

interface Pendencia {
  id: string; tipo: string; motivo: string; usuario: string;
  dispositivo: string | null; registrado_em: string; recebido_em: string;
  payload: Record<string, unknown>;
}

/**
 * O que o pátio mandou e o servidor recusou.
 *
 * Spec §5: recusa não some. Vira pendência com motivo legível, numa fila que
 * alguém resolve no escritório — app que engole o trabalho de campo perde a
 * confiança do operador na primeira semana. Esta é a metade do escritório.
 */
export function Pendencias() {
  const [lista, setLista] = useState<Pendencia[] | null>(null);

  useEffect(() => {
    void api
      .get<{ pendencias: Pendencia[] }>("/sync/pendencias")
      .then((r) => setLista(r.pendencias))
      .catch(() => setLista([]));
  }, []);

  if (lista !== null && lista.length === 0) return null;

  return (
    <div className="painel" style={{ maxWidth: 900, borderColor: "var(--aten-borda)" }}>
      <div className="tt" style={{ background: "var(--aten-fundo)", display: "flex", gap: 10, alignItems: "center" }}>
        <span className="rotulo" style={{ color: "var(--aten-texto)" }}>
          Vindo do pátio · recusado
        </span>
        {lista && <span className="pil p-aten"><i />{lista.length}</span>}
        <span className="cod" style={{ marginLeft: "auto" }}>
          O aparelho já limpou da fila local — o fato está guardado aqui
        </span>
      </div>
      {lista === null ? (
        <div className="esq">
          {[0, 1].map((i) => (
            <div className="l" key={i}>
              <span className="b" /><span className="b" /><span className="b" /><span className="b" />
            </div>
          ))}
        </div>
      ) : (
        <table className="tab">
          <thead>
            <tr>
              <th>Coletado</th><th>Operador</th><th>Aparelho</th>
              <th>Tipo</th><th>Motivo da recusa</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((p) => (
              <tr key={p.id}>
                <td className="mono">
                  {new Date(p.registrado_em).toLocaleString("pt-BR", {
                    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                  })}
                </td>
                <td>{p.usuario}</td>
                <td className="cod">{p.dispositivo ?? "—"}</td>
                <td className="mono" style={{ fontSize: 12 }}>{p.tipo}</td>
                <td style={{ color: "var(--erro-texto)" }}>{p.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
